import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import {
  CreateCommentPayload,
  TaskComment,
  TaskCommentView,
} from "@models/task-comment";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root",
})
export class CommentsService {
  private readonly _http = inject(HttpClient);
  private readonly _authService = inject(AuthService);
  private readonly baseUrl = `${environment.API_BACKEND}/comments`;

  getAll(
    commentableType: string,
    commentableId: number,
  ): Observable<TaskCommentView[]> {
    const params = new HttpParams()
      .set("commentable_type", commentableType)
      .set("commentable_id", commentableId);

    return this._http.get<TaskComment[]>(this.baseUrl, { params }).pipe(
      map((comments) => {
        const currentUserId =
          this._authService.getLocalStorageUserInfo()?.id;
        return comments.map((comment) => ({
          ...comment,
          isCurrentUser: comment.user_id === currentUserId,
        }));
      }),
    );
  }

  getById(
    id: number,
    commentableType: string,
    commentableId: number,
  ): Observable<TaskComment> {
    const params = new HttpParams()
      .set("commentable_type", commentableType)
      .set("commentable_id", commentableId);

    return this._http.get<TaskComment>(`${this.baseUrl}/${id}`, {
      params,
    });
  }

  create(payload: CreateCommentPayload): Observable<TaskComment> {
    const params = new HttpParams()
      .set("commentable_type", payload.commentable_type)
      .set("commentable_id", payload.commentable_id);

    return this._http.post<TaskComment>(this.baseUrl, payload, {
      params,
    });
  }

  update(
    id: number,
    commentableType: string,
    commentableId: number,
    body: Partial<CreateCommentPayload>,
  ): Observable<TaskComment> {
    const params = new HttpParams()
      .set("commentable_type", commentableType)
      .set("commentable_id", commentableId);

    return this._http.patch<TaskComment>(
      `${this.baseUrl}/${id}`,
      body,
      { params },
    );
  }

  delete(
    id: number,
    commentableType: string,
    commentableId: number,
  ): Observable<void> {
    const params = new HttpParams()
      .set("commentable_type", commentableType)
      .set("commentable_id", commentableId);

    return this._http.delete<void>(`${this.baseUrl}/${id}`, {
      params,
    });
  }
}
